import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Trash2 } from 'lucide-react';

import type { ClientOptionModel, ProjectOptionModel } from '@/domain/models/user.js';
import type { AssignmentRow } from '@/presentation/pages/users/utils/validate-user-form.js';
import type { UserProfile } from '@/shared/utils/format-status.js';

interface Props {
    assignment: AssignmentRow;
    clients: ClientOptionModel[];
    projects: ProjectOptionModel[];
    profiles: { value: UserProfile; label: string }[];
    canRemove: boolean;
    error?: string;
    labels: { client: string; projectOptional: string; profileLabel: string; selectClient: string; allProjectsOption: string; removeAssignment: string };
    onChange: (field: keyof AssignmentRow, value: string) => void;
    onRemove: () => void;
}

export function UserAssignmentCard({ assignment, clients, projects, profiles, canRemove, error, labels, onChange, onRemove }: Props) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1, bgcolor: 'action.hover' }}>
            <CardContent sx={{ p: 2, '&:last-of-type': { pb: 2 } }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            select
                            data-testid="user-assignment-card-client"
                            label={labels.client}
                            value={assignment.clientId}
                            onChange={(e) => onChange('clientId', e.target.value)}
                            size="small"
                            fullWidth
                        >
                            <MenuItem value="">{labels.selectClient}</MenuItem>
                            {clients.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            select
                            data-testid="user-assignment-card-project"
                            label={labels.projectOptional}
                            value={assignment.projectId}
                            onChange={(e) => onChange('projectId', e.target.value)}
                            size="small"
                            fullWidth
                            disabled={!assignment.clientId}
                        >
                            <MenuItem value="">{labels.allProjectsOption}</MenuItem>
                            {projects.map((p) => (
                                <MenuItem key={p.id} value={p.id}>
                                    {p.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                            select
                            label={labels.profileLabel}
                            value={assignment.profile}
                            onChange={(e) => onChange('profile', e.target.value)}
                            size="small"
                            fullWidth
                            error={!!error}
                            helperText={error}
                        >
                            {profiles.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 1 }}>
                        {canRemove && (
                            <IconButton data-testid="user-assignment-card-remove" aria-label={labels.removeAssignment} size="small" color="error" onClick={onRemove}>
                                <Trash2 size={16} />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
